import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTenant } from '../../contexts/TenantContext'
import { useAuth } from '../../contexts/AuthContext'
import CompactEventForm from '../../components/client-admin/CompactEventForm'
import { buildApiUrl, makeAuthenticatedRequest } from '../../config/api'
import '../../styles/client-admin/dashboard-theme.scss'
import '../../styles/client-admin/events.scss'
import '../../styles/client-admin/compact-event-form.scss'
import '../../styles/client-admin/professional-event-management.scss'

const ProfessionalEventManagement = ({ filter }) => {
  const navigate = useNavigate()
  const { tenant } = useTenant()
  const { token } = useAuth()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [stats, setStats] = useState({
    total: 0,
    published: 0,
    draft: 0,
    upcoming: 0,
    completed: 0,
    ongoing: 0,
    cancelled: 0
  })
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [editingEvent, setEditingEvent] = useState(null)
  const [formLoading, setFormLoading] = useState(false)
  const [viewMode, setViewMode] = useState('grid')
  const [sortBy, setSortBy] = useState('event_start_date')
  const [sortOrder, setSortOrder] = useState('desc')
  const [selectedEvents, setSelectedEvents] = useState([])
  const [showBulkActions, setShowBulkActions] = useState(false)
  // Bulk import removed as per user request
  const [showTemplates, setShowTemplates] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [confirmAction, setConfirmAction] = useState(null)
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    event_type: '',
    event_level: '',
    date_range: '',
    organizer: ''
  })
  const [showAnalytics, setShowAnalytics] = useState(false)
  const [showRegistrationsModal, setShowRegistrationsModal] = useState(false)
  const [selectedEventRegistrations, setSelectedEventRegistrations] = useState(null)
  const [registrations, setRegistrations] = useState([])
  const [registrationsLoading, setRegistrationsLoading] = useState(false)
  const [showMoreMenu, setShowMoreMenu] = useState(null)
  const [selectedYear, setSelectedYear] = useState(null)

  const API_BASE = buildApiUrl('/client-admin')

  // Get available years from past events
  const getAvailableYears = () => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    const pastEvents = events.filter(event => {
      const endDate = new Date(event.event_end_date)
      endDate.setHours(23, 59, 59, 999)
      return endDate < today
    })

    const years = [...new Set(pastEvents.map(event => {
      return new Date(event.event_end_date).getFullYear()
    }))].sort((a, b) => b - a)

    return years
  }

  // Filter events based on past/upcoming and year
  // End date ke next day tournament past me chala jayega
  // e.g. end date 18 Feb → 19 Feb ko past me
  const getFilteredEvents = () => {
    if (!filter) return events

    const today = new Date()
    today.setHours(0, 0, 0, 0)

    let result = events.filter(event => {
      const endDate = new Date(event.event_end_date)
      endDate.setHours(23, 59, 59, 999)

      if (filter === 'past') {
        return endDate < today
      } else if (filter === 'upcoming') {
        return endDate >= today
      }
      return true
    })

    // Apply year filter for past tournaments
    if (filter === 'past' && selectedYear) {
      result = result.filter(event => {
        const eventYear = new Date(event.event_end_date).getFullYear()
        return eventYear === selectedYear
      })
    }

    return result
  }

  const filteredEvents = getFilteredEvents()
  const availableYears = getAvailableYears()

  // Check if event is past (for disabling actions)
  const isEventPast = (event) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const endDate = new Date(event.event_end_date)
    endDate.setHours(23, 59, 59, 999)
    return endDate < today
  }

  // Editing is locked once the tournament start date has arrived/passed.
  // (A tournament that has already started or finished should not be edited.)
  const isEditLocked = (event) => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    const startDate = new Date(event.event_start_date)
    startDate.setHours(0, 0, 0, 0)
    return startDate <= today
  }

  // Get page title based on filter
  const getPageTitle = () => {
    if (filter === 'past') return 'Past Tournaments'
    if (filter === 'upcoming') return 'Upcoming Tournaments'
    return 'Tournament Management'
  }

  useEffect(() => {
    loadEvents()
  }, [filters])

  // Past/Upcoming routes reuse this same component instance, so switching the
  // sidebar link only changes the `filter` prop. Close any open create/edit
  // form so the requested list actually shows instead of the leftover form.
  useEffect(() => {
    setShowCreateForm(false)
    setEditingEvent(null)
  }, [filter])

  useEffect(() => {
    const handleClickOutside = () => {
      if (showMoreMenu) {
        setShowMoreMenu(null)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [showMoreMenu])

  const loadEvents = async () => {
    try {
      setLoading(true)

      console.log('🔄 Loading events from Laravel API...')

      const queryParams = new URLSearchParams()
      Object.keys(filters).forEach(key => {
        if (filters[key]) queryParams.append(key, filters[key])
      })

      const apiUrl = `${API_BASE}/events?${queryParams}`
      console.log('📡 API URL:', apiUrl)

      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })

      console.log('📈 Response status:', response.status)
      console.log('📋 Response headers:', [...response.headers.entries()])
      
      if (response.ok) {
        const data = await response.json()
        console.log('✅ API Response data:', data)
        
        setEvents(data.events.data || [])
        setStats({
          ...stats,
          ...data.stats
        })
        // Events loaded successfully (no notification needed)
      } else if (response.status === 401) {
        // Session expired - send the admin back to login instead of a misleading connection error
        showNotification('Your session has expired. Redirecting to login...', 'error')
        localStorage.removeItem('tenant_admin_token')
        setTimeout(() => { window.location.href = '/admin/login' }, 1200)
      } else {
        const errorText = await response.text()
        console.error('❌ Failed to load events:', response.status, response.statusText)
        console.error('❌ Error response:', errorText)
        showNotification('Failed to load events. Please check your connection.', 'error')
      }
    } catch (error) {
      console.error('❌ Network error loading events:', error)
      showNotification('Network error: ' + error.message, 'error')
    } finally {
      setLoading(false)
    }
  }


  // CRUD Operations
  const handleCreateEvent = async (formData) => {
    try {
      setFormLoading(true)

      console.log('🆕 Creating new event via Laravel API...')
      console.log('📝 Form data entries:')
      for (let [key, value] of formData.entries()) {
        console.log(`  ${key}: ${value}`)
      }

      const response = await fetch(`${API_BASE}/events`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      })

      console.log('📈 Create response status:', response.status)

      if (response.ok) {
        const result = await response.json()
        console.log('✅ Create response:', result)

        setShowCreateForm(false)
        await loadEvents() // Reload events list
        showNotification('Event created successfully!', 'success')
      } else {
        const errorData = await response.json()
        console.error('❌ Create error response:', errorData)
        showNotification('Failed to create event: ' + (errorData.message || 'Unknown error'), 'error')
      }
    } catch (error) {
      console.error('❌ Network error creating event:', error)
      showNotification('Network error: ' + error.message, 'error')
    } finally {
      setFormLoading(false)
    }
  }

  const handleUpdateEvent = async (formData) => {
    try {
      setFormLoading(true)

      console.log('📝 Updating event via Laravel API:', editingEvent.id)

      const response = await fetch(`${API_BASE}/events/${editingEvent.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(Object.fromEntries(formData))
      })

      if (response.ok) {
        const result = await response.json()
        console.log('✅ Update response:', result)
        setEditingEvent(null)
        loadEvents()
        showNotification('Event updated successfully!', 'success')
      } else {
        const errorData = await response.json()
        console.error('❌ Update error:', errorData)
        showNotification('Failed to update event: ' + (errorData.message || 'Unknown error'), 'error')
      }
    } catch (error) {
      console.error('❌ Error updating event:', error)
      showNotification('Failed to update event: ' + error.message, 'error')
    } finally {
      setFormLoading(false)
    }
  }

  const handleDeleteEvent = async (eventId) => {
    if (!confirm('Are you sure you want to delete this event?')) return

    try {
      console.log('🗑️ Deleting event via Laravel API:', eventId)

      const response = await fetch(`${API_BASE}/events/${eventId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })
      
      if (response.ok) {
        const result = await response.json()
        console.log('✅ Delete response:', result)
        loadEvents()
        showNotification('Event deleted successfully!', 'success')
      } else {
        const errorData = await response.json()
        console.error('❌ Delete error:', errorData)
        showNotification('Failed to delete event: ' + (errorData.message || 'Unknown error'), 'error')
      }
    } catch (error) {
      showNotification('Failed to delete event: ' + error.message, 'error')
    }
  }

  const handleStatusUpdate = async (eventId, newStatus) => {
    try {
      console.log('🔄 Updating event status via Laravel API:', eventId, newStatus)

      const response = await fetch(`${API_BASE}/events/${eventId}/status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          status: newStatus,
          is_published: newStatus === 'published'
        })
      })

      if (response.ok) {
        const result = await response.json()
        console.log('✅ Status update response:', result)
        loadEvents()
        showNotification(`Event ${newStatus} successfully!`, 'success')
      } else {
        const errorData = await response.json()
        console.error('❌ Status update error:', errorData)
        showNotification('Failed to update event status: ' + (errorData.message || 'Unknown error'), 'error')
      }
    } catch (error) {
      console.error('❌ Error updating event status:', error)
      showNotification('Failed to update event status: ' + error.message, 'error')
    }
  }

  const showNotification = (message, type) => {
    // Create and show notification
    const notification = document.createElement('div')
    notification.className = `message-popup ${type}`
    notification.innerHTML = `
      <i class="fas fa-${type === 'success' ? 'check-circle' : 'exclamation-circle'}"></i>
      ${message}
    `
    document.body.appendChild(notification)
    
    setTimeout(() => {
      notification.remove()
    }, 4000)
  }

  const getStatusBadge = (status) => {
    const badges = {
      draft: { class: 'status-draft', text: 'Draft', icon: 'fas fa-edit' },
      published: { class: 'status-published', text: 'Published', icon: 'fas fa-eye' },
      registration_open: { class: 'status-open', text: 'Registration Open', icon: 'fas fa-door-open' },
      registration_closed: { class: 'status-reg-closed', text: 'Registration Closed', icon: 'fas fa-user-times' },
      ongoing: { class: 'status-ongoing', text: 'Ongoing', icon: 'fas fa-play' },
      completed: { class: 'status-completed', text: 'Completed', icon: 'fas fa-check' },
      cancelled: { class: 'status-cancelled', text: 'Event Closed', icon: 'fas fa-times-circle' }
    }
    return badges[status] || badges.draft
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    })
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }))
  }

  const exportData = (format) => {
    showNotification(`Exporting data as ${format}...`, 'info')
    // Implementation for export functionality
  }

  const testConnection = async () => {
    try {
      console.log('🔍 Testing Laravel API connection...')
      showNotification('Testing connection...', 'info')

      const apiUrl = `${API_BASE}/events`
      const response = await fetch(apiUrl, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      })
      
      if (response.ok) {
        const data = await response.json()
        console.log('✅ Connection test successful:', data)
        showNotification(`Connection successful! Found ${data.events.data?.length || 0} events.`, 'success')
        
        // Auto create sample events if database is empty
        if (!data.events.data || data.events.data.length === 0) {
          if (confirm('No events found. Would you like to create some sample events?')) {
            await createSampleEvents()
          }
        }
      } else {
        console.error('❌ Connection test failed:', response.status, response.statusText)
        showNotification(`Connection failed: ${response.status} ${response.statusText}`, 'error')
      }
    } catch (error) {
      console.error('❌ Connection test error:', error)
      showNotification(`Connection error: ${error.message}`, 'error')
    }
  }

  const createSampleEvents = async () => {
    const sampleEvents = [
      {
        event_name: 'Mumbai Cricket Championship 2024',
        event_organizer: 'Mumbai Cricket Association',
        event_venue: 'Wankhede Stadium',
        event_city: 'Mumbai',
        event_state: 'Maharashtra',
        event_start_date: '2024-10-15',
        event_end_date: '2024-10-25',
        event_type: 'N',
        event_level: 'SL',
        event_description: 'Annual cricket championship featuring top teams from Mumbai region.'
      },
      {
        event_name: 'Junior Baseball Tournament',
        event_organizer: 'Delhi Baseball Federation',
        event_venue: 'Delhi Sports Complex',
        event_city: 'Delhi',
        event_state: 'Delhi',
        event_start_date: '2024-11-01',
        event_end_date: '2024-11-05',
        event_type: 'N',
        event_level: 'JL',
        event_description: 'Regional junior baseball tournament for young players.'
      },
      {
        event_name: 'International Baseball Cup',
        event_organizer: 'Indian Baseball Federation',
        event_venue: 'Bengaluru International Stadium',
        event_city: 'Bengaluru',
        event_state: 'Karnataka',
        event_start_date: '2024-12-10',
        event_end_date: '2024-12-20',
        event_type: 'IN',
        event_level: 'SL',
        event_description: 'International baseball championship with teams from multiple countries.'
      }
    ]

    for (let eventData of sampleEvents) {
      const formData = new FormData()
      Object.keys(eventData).forEach(key => {
        formData.append(key, eventData[key])
      })

      try {
        await handleCreateEvent(formData)
        await new Promise(resolve => setTimeout(resolve, 500)) // Small delay between creates
      } catch (error) {
        console.error('Error creating sample event:', error)
      }
    }
    
    showNotification('Sample events created successfully!', 'success')
  }

  const eventTemplates = [
    {
      id: 'cricket_tournament',
      name: 'Cricket Tournament',
      description: 'Standard cricket tournament template',
      data: {
        event_name: 'Cricket Championship',
        event_organizer: 'Cricket Association',
        event_venue: 'Cricket Stadium',
        event_city: 'Mumbai',
        event_state: 'Maharashtra',
        event_type: 'N',
        event_level: 'SL',
        event_description: 'Annual cricket tournament featuring top teams.'
      }
    },
    {
      id: 'baseball_league',
      name: 'Baseball League',
      description: 'Baseball league season template',
      data: {
        event_name: 'Baseball League Season',
        event_organizer: 'Baseball Federation',
        event_venue: 'Baseball Ground',
        event_city: 'Delhi',
        event_state: 'Delhi',
        event_type: 'N',
        event_level: 'SL',
        event_description: 'Professional baseball league season.'
      }
    },
    {
      id: 'youth_tournament',
      name: 'Youth Tournament',
      description: 'Junior level sports tournament',
      data: {
        event_name: 'Youth Sports Tournament',
        event_organizer: 'Youth Sports Council',
        event_venue: 'Youth Sports Complex',
        event_city: 'Bangalore',
        event_state: 'Karnataka',
        event_type: 'N',
        event_level: 'JL',
        event_description: 'Tournament for young athletes under 18.'
      }
    },
    {
      id: 'international_cup',
      name: 'International Cup',
      description: 'International level championship',
      data: {
        event_name: 'International Championship',
        event_organizer: 'International Sports Federation',
        event_venue: 'International Stadium',
        event_city: 'New Delhi',
        event_state: 'Delhi',
        event_type: 'IN',
        event_level: 'SL',
        event_description: 'International championship with global participation.'
      }
    }
  ]

  const useTemplate = (template) => {
    const templateData = { ...template.data }
    templateData.event_start_date = new Date().toISOString().split('T')[0]
    const endDate = new Date()
    endDate.setDate(endDate.getDate() + 7)
    templateData.event_end_date = endDate.toISOString().split('T')[0]
    
    // Create form data and trigger create
    const formData = new FormData()
    Object.keys(templateData).forEach(key => {
      formData.append(key, templateData[key])
    })
    
    handleCreateEvent(formData)
    setShowTemplates(false)
    showNotification(`Event created using ${template.name} template!`, 'success')
  }

  const handleAnalyticsClick = () => {
    setShowAnalytics(!showAnalytics)
  }

  const handleCloseRegistration = (eventId, eventName) => {
    setConfirmAction({
      type: 'closeRegistration',
      eventId: eventId,
      eventName: eventName,
      title: 'Close Registration',
      message: `Are you sure you want to close registrations for this event?`,
      subMessage: `Event: ${eventName}`,
      confirmText: 'Yes, Close Registration',
      cancelText: 'No, Keep Open'
    })
    setShowConfirmDialog(true)
  }

  const handleCloseEvent = (eventId, eventName) => {
    setConfirmAction({
      type: 'closeEvent',
      eventId: eventId,
      eventName: eventName,
      title: 'Close Event',
      message: `Are you sure you want to close this event?`,
      subMessage: `Event: ${eventName}`,
      confirmText: 'Yes, Close Event',
      cancelText: 'No, Keep Active'
    })
    setShowConfirmDialog(true)
  }

  const executeConfirmAction = async () => {
    if (!confirmAction) return

    try {
      setFormLoading(true)
      
      if (confirmAction.type === 'closeRegistration') {
        // Update event to close registration using Laravel API
        const response = await fetch(`${API_BASE}/events/${confirmAction.eventId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            registration_closed: true,
            status: 'registration_closed'
          })
        })

        if (response.ok) {
          await loadEvents()
          showNotification('Event registration closed successfully!', 'success')
        } else {
          const errorData = await response.json()
          showNotification('Failed to close registration: ' + (errorData.message || 'Unknown error'), 'error')
        }

      } else if (confirmAction.type === 'closeEvent') {
        // Update event status to cancelled using Laravel API
        const response = await fetch(`${API_BASE}/events/${confirmAction.eventId}/status`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            status: 'cancelled',
            is_published: false
          })
        })

        if (response.ok) {
          await loadEvents()
          showNotification('Event closed successfully!', 'success')
        } else {
          const errorData = await response.json()
          showNotification('Failed to close event: ' + (errorData.message || 'Unknown error'), 'error')
        }
      }
    } catch (error) {
      console.error('Error executing action:', error)
      showNotification('Error: ' + error.message, 'error')
    } finally {
      setFormLoading(false)
      setShowConfirmDialog(false)
      setConfirmAction(null)
    }
  }

  const handleViewRegistrations = async (event) => {
    try {
      setRegistrationsLoading(true)
      setSelectedEventRegistrations(event)
      setShowRegistrationsModal(true)

      const response = await fetch(`${API_BASE}/events/${event.id}/registrations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        setRegistrations(data.data.registrations || [])
      } else {
        showNotification('Failed to load registrations', 'error')
      }
    } catch (error) {
      console.error('Error loading registrations:', error)
      showNotification('Error loading registrations', 'error')
    } finally {
      setRegistrationsLoading(false)
    }
  }

  const handleApproveRegistration = async (playerId) => {
    if (!selectedEventRegistrations) return

    try {
      const response = await fetch(`${API_BASE}/events/${selectedEventRegistrations.id}/registrations/${playerId}/approve`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        showNotification('Player registration approved!', 'success')
        // Reload registrations
        handleViewRegistrations(selectedEventRegistrations)
      } else {
        showNotification('Failed to approve registration', 'error')
      }
    } catch (error) {
      console.error('Error approving registration:', error)
      showNotification('Error approving registration', 'error')
    }
  }

  const handleRejectRegistration = async (playerId) => {
    if (!selectedEventRegistrations) return

    if (!confirm('Are you sure you want to reject this player registration?')) return

    try {
      const response = await fetch(`${API_BASE}/events/${selectedEventRegistrations.id}/registrations/${playerId}/reject`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      })

      if (response.ok) {
        showNotification('Player registration rejected!', 'success')
        // Reload registrations
        handleViewRegistrations(selectedEventRegistrations)
      } else {
        showNotification('Failed to reject registration', 'error')
      }
    } catch (error) {
      console.error('Error rejecting registration:', error)
      showNotification('Error rejecting registration', 'error')
    }
  }

  // Show form if creating or editing
  if (showCreateForm || editingEvent) {
    return (
      <CompactEventForm
        event={editingEvent}
        onSubmit={editingEvent ? handleUpdateEvent : handleCreateEvent}
        onCancel={() => {
          setShowCreateForm(false)
          setEditingEvent(null)
        }}
        isLoading={formLoading}
      />
    )
  }


  return (
    <div className="dashboard-layout professional-layout">
      <div className="dashboard-container">
        {/* Page Header */}
        <div className="page-header professional-header">
          <div className="header-content">
            <div className="header-left">
              <div className="page-breadcrumb">
                <span className="breadcrumb-item">Dashboard</span>
                <i className="fas fa-chevron-right"></i>
                <span className="breadcrumb-item active">{getPageTitle()}</span>
              </div>
              <h1 className="page-title">
                <div className="page-icon">
                  <i className="fas fa-calendar-alt"></i>
                </div>
                {getPageTitle()}
              </h1>
              <p className="page-subtitle">
                Manage tournaments and competitions for {tenant?.name || 'your organization'}
              </p>
            </div>
            <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
              <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
                <i className="fas fa-plus"></i>
                <span>Create New Event</span>
              </button>
              <button
                onClick={() => navigate('/admin/dashboard')}
                style={{
                  background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
                  border: 'none',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '10px 20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85rem',
                  fontWeight: '600',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 8px rgba(192, 15, 21, 0.4)'
                }}
              >
                <i className="fas fa-arrow-left"></i> Back
              </button>
            </div>
          </div>
        </div>

        {/* Page Content */}
        <div className="page-content professional-content">
          {/* Search Section */}
          <div className="search-section" style={{
            background: 'white',
            padding: '1rem',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            marginBottom: '1.5rem'
          }}>
            <div style={{ position: 'relative' }}>
              <i className="fas fa-search" style={{
                position: 'absolute',
                left: '1rem',
                top: '50%',
                transform: 'translateY(-50%)',
                color: '#9ca3af'
              }}></i>
              <input
                type="text"
                placeholder="Search tournaments by name, venue, or organizer..."
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="search-input"
                style={{
                  width: '100%',
                  padding: '0.85rem 1rem 0.85rem 2.75rem',
                  fontSize: '1rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '8px',
                  outline: 'none',
                  transition: 'all 0.2s'
                }}
              />
            </div>
          </div>

          {false && (
            <div className="analytics-dashboard">
            <div className="stats-overview">
              <div className="stats-grid">
                <div className="stat-card primary">
                  <div className="stat-content">
                    <div className="stat-info">
                      <div className="stat-value">{stats.total}</div>
                      <div className="stat-label">Total Tournaments</div>
                      <div className="stat-change positive">+12% from last month</div>
                    </div>
                    <div className="stat-icon">
                      <i className="fas fa-calendar"></i>
                    </div>
                  </div>
                  <div className="stat-chart">
                    <div className="progress-bar">
                      <div className="progress-fill" style={{width: '85%'}}></div>
                    </div>
                  </div>
                </div>
                
                <div className="stat-card success">
                  <div className="stat-content">
                    <div className="stat-info">
                      <div className="stat-value">{stats.published}</div>
                      <div className="stat-label">Published</div>
                      <div className="stat-change positive">+8% this week</div>
                    </div>
                    <div className="stat-icon">
                      <i className="fas fa-eye"></i>
                    </div>
                  </div>
                  <div className="stat-chart">
                    <div className="progress-bar">
                      <div className="progress-fill success" style={{width: '92%'}}></div>
                    </div>
                  </div>
                </div>
                
                <div className="stat-card warning">
                  <div className="stat-content">
                    <div className="stat-info">
                      <div className="stat-value">{stats.ongoing}</div>
                      <div className="stat-label">Ongoing</div>
                      <div className="stat-change neutral">Live tournaments</div>
                    </div>
                    <div className="stat-icon">
                      <i className="fas fa-play"></i>
                    </div>
                  </div>
                  <div className="stat-chart">
                    <div className="progress-bar">
                      <div className="progress-fill warning" style={{width: '65%'}}></div>
                    </div>
                  </div>
                </div>
                
                <div className="stat-card info">
                  <div className="stat-content">
                    <div className="stat-info">
                      <div className="stat-value">{stats.completed}</div>
                      <div className="stat-label">Completed</div>
                      <div className="stat-change neutral">{stats.draft} drafts pending</div>
                    </div>
                    <div className="stat-icon">
                      <i className="fas fa-check-circle"></i>
                    </div>
                  </div>
                  <div className="stat-chart">
                    <div className="progress-bar">
                      <div className="progress-fill info" style={{width: '78%'}}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="quick-actions">
              <div className="action-card" onClick={() => setShowCreateForm(true)}>
                <i className="fas fa-plus-circle"></i>
                <h4>Quick Create</h4>
                <p>Start a new tournament</p>
                <button className="btn btn-primary">Create</button>
              </div>
              <div className="action-card" onClick={() => setShowBulkImport(true)}>
                <i className="fas fa-upload"></i>
                <h4>Bulk Import</h4>
                <p>Import multiple tournaments</p>
                <button className="btn btn-secondary">Import</button>
              </div>
              <div className="action-card" onClick={() => setShowTemplates(true)}>
                <i className="fas fa-calendar-check"></i>
                <h4>Templates</h4>
                <p>Use tournament templates</p>
                <button className="btn btn-secondary">Browse</button>
              </div>
            </div>
          </div>
          )}

          {/* Events Section */}
          <div className="content-section" style={{
            background: 'white',
            borderRadius: '12px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
            overflow: 'hidden'
          }}>
            <div className="section-header" style={{
              background: 'linear-gradient(to right, #f8fafc, #f1f5f9)',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #e5e7eb',
              margin: 0
            }}>
              <h2 className="section-title" style={{ margin: 0, fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <i className="fas fa-calendar-alt"></i>
                {getPageTitle()}
              </h2>
              <div className="section-meta">
                <span className="item-count" style={{
                  background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
                  color: 'white',
                  padding: '0.4rem 1rem',
                  borderRadius: '20px',
                  fontSize: '0.85rem',
                  fontWeight: '600'
                }}>{filteredEvents.length} tournaments found</span>
              </div>
            </div>

            {/* Year-wise tabs for Past Tournaments */}
            {filter === 'past' && availableYears.length > 0 && (
              <div style={{
                padding: '1rem 1.5rem',
                borderBottom: '1px solid #e5e7eb',
                background: '#fafafa',
                display: 'flex',
                gap: '8px',
                flexWrap: 'wrap',
                alignItems: 'center'
              }}>
                <span style={{ fontSize: '0.85rem', color: '#6b7280', fontWeight: '500', marginRight: '8px' }}>
                  Filter by Year:
                </span>
                {availableYears.map(year => (
                  <button
                    key={year}
                    onClick={() => setSelectedYear(year)}
                    style={{
                      padding: '6px 16px',
                      borderRadius: '20px',
                      border: selectedYear === year ? '2px solid #c00f15' : '1px solid #e5e7eb',
                      background: selectedYear === year ? '#fef2f2' : 'white',
                      color: selectedYear === year ? '#c00f15' : '#4b5563',
                      fontWeight: selectedYear === year ? '600' : '500',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      transition: 'all 0.2s'
                    }}
                  >
                    {year}
                  </button>
                ))}
              </div>
            )}

            {loading ? (
              <div className="dashboard-card loading-state">
                <div className="card-body">
                  <div className="loading-content">
                    <div className="loading-spinner">
                      <i className="fas fa-spinner fa-spin"></i>
                    </div>
                    <h3>Loading Tournaments</h3>
                    <p>Fetching your tournament data...</p>
                  </div>
                </div>
              </div>
            ) : filteredEvents.length === 0 ? (
              <div style={{
                padding: '3rem 1.5rem',
                textAlign: 'center',
                background: '#fafbfc'
              }}>
                <div style={{
                  width: '80px',
                  height: '80px',
                  borderRadius: '50%',
                  background: filter === 'past' ? 'linear-gradient(135deg, #fee2e2, #fecaca)' : 'linear-gradient(135deg, #dbeafe, #bfdbfe)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 1.25rem'
                }}>
                  <i className={filter === 'past' ? 'fas fa-history' : filter === 'upcoming' ? 'fas fa-calendar-alt' : 'fas fa-calendar-plus'} style={{
                    fontSize: '2rem',
                    color: filter === 'past' ? '#dc2626' : '#2563eb'
                  }}></i>
                </div>
                <h3 style={{
                  fontSize: '1.25rem',
                  fontWeight: '700',
                  color: '#1f2937',
                  margin: '0 0 0.5rem'
                }}>
                  {events.length === 0
                    ? 'No Tournaments Created Yet'
                    : filter === 'past'
                      ? 'No Past Tournaments'
                      : filter === 'upcoming'
                        ? 'No Upcoming Tournaments'
                        : 'No Tournaments Found'}
                </h3>
                <p style={{
                  fontSize: '0.9rem',
                  color: '#6b7280',
                  margin: '0 0 1.5rem',
                  maxWidth: '400px',
                  marginLeft: 'auto',
                  marginRight: 'auto'
                }}>
                  {events.length === 0
                    ? 'Get started by creating your first tournament.'
                    : filter === 'past'
                      ? 'There are no completed tournaments yet. Check back after your upcoming events finish.'
                      : filter === 'upcoming'
                        ? 'No upcoming tournaments scheduled. Create a new event to get started.'
                        : 'No tournaments match your current filters.'}
                </p>
                <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
                  <button
                    className="btn btn-primary"
                    onClick={() => setShowCreateForm(true)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '10px 24px',
                      background: 'linear-gradient(135deg, #c00f15 0%, #a00d12 100%)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      fontWeight: '600',
                      fontSize: '0.9rem',
                      cursor: 'pointer'
                    }}
                  >
                    <i className="fas fa-plus"></i>
                    Create New Tournament
                  </button>
                  {filter === 'past' && events.length > 0 && (
                    <button
                      onClick={() => navigate('/admin/events/upcoming')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 24px',
                        background: 'white',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        cursor: 'pointer'
                      }}
                    >
                      <i className="fas fa-arrow-right"></i>
                      View Upcoming Tournaments
                    </button>
                  )}
                  {filter === 'upcoming' && events.length > 0 && (
                    <button
                      onClick={() => navigate('/admin/events/past')}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '10px 24px',
                        background: 'white',
                        color: '#374151',
                        border: '1px solid #d1d5db',
                        borderRadius: '8px',
                        fontWeight: '600',
                        fontSize: '0.9rem',
                        cursor: 'pointer'
                      }}
                    >
                      <i className="fas fa-arrow-left"></i>
                      View Past Tournaments
                    </button>
                  )}
                </div>
              </div>
            ) : (
              <div style={{ border: 'none', boxShadow: 'none', overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'linear-gradient(to right, #022a5e, #053e88, #022a5e)' }}>
                      <th style={{ color: '#ffffff', padding: '14px 10px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'left', background: 'transparent' }}>Event Name</th>
                      <th style={{ color: '#ffffff', padding: '14px 10px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'left', background: 'transparent' }}>Venue</th>
                      <th style={{ color: '#ffffff', padding: '14px 10px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'left', background: 'transparent' }}>Organizer</th>
                      <th style={{ color: '#ffffff', padding: '14px 10px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'left', background: 'transparent' }}>Date</th>
                      <th style={{ color: '#ffffff', padding: '14px 10px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'left', background: 'transparent' }}>Category</th>
                      <th style={{ color: '#ffffff', padding: '14px 10px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'left', background: 'transparent' }}>Teams & Results</th>
                      <th style={{ color: '#ffffff', padding: '14px 10px', fontSize: '0.75rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.03em', textAlign: 'center', background: 'transparent' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredEvents.map(event => (
                      <tr key={event.id} style={{ transition: 'background 0.2s' }}>
                        <td style={{ fontWeight: '600', color: '#1f2937', padding: '12px 10px' }}>{event.event_name}</td>
                        <td style={{ color: '#4b5563', padding: '12px 10px' }}>{event.event_venue}</td>
                        <td style={{ color: '#4b5563', padding: '12px 10px' }}>{event.event_organizer}</td>
                        <td style={{ color: '#4b5563', whiteSpace: 'nowrap', padding: '12px 10px' }}>
                          {`${formatDate(event.event_start_date)} - ${formatDate(event.event_end_date)}`}
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          {event.allowed_categories && event.allowed_categories.length > 0 ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {event.allowed_categories.map((cat, idx) => (
                                <span key={idx} style={{
                                  display: 'inline-block',
                                  padding: '3px 8px',
                                  background: '#f0fdf4',
                                  color: '#166534',
                                  borderRadius: '4px',
                                  fontSize: '11px',
                                  fontWeight: '500'
                                }}>
                                  {cat.code} ({cat.genders?.map(g => g === 'M' ? 'M' : 'F').join('/')})
                                </span>
                              ))}
                            </div>
                          ) : (
                            <span style={{
                              display: 'inline-block',
                              padding: '5px 10px',
                              background: '#f0fdf4',
                              color: '#166534',
                              borderRadius: '6px',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}>
                              {event.level_name || '-'}
                            </span>
                          )}
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                            <button
                              onClick={() => navigate(`/admin/events/${event.id}/fixtures`)}
                              title="Build or view the draw and match schedule"
                              style={{
                                padding: '6px 12px',
                                background: 'linear-gradient(135deg, #7c3aed, #6d28d9)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 600,
                                whiteSpace: 'nowrap'
                              }}
                            >
                              Draw
                            </button>
                            <button
                              className="player-count-btn"
                              onClick={() => navigate(`/admin/events/${event.id}/registrations`)}
                              style={{
                                padding: '6px 12px',
                                background: event.player_counts?.total > 0
                                  ? 'linear-gradient(135deg, #3b82f6, #2563eb)'
                                  : 'linear-gradient(135deg, #6b7280, #4b5563)',
                                color: 'white',
                                border: 'none',
                                borderRadius: '6px',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: '600',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                transition: 'all 0.2s'
                              }}
                              title="View Teams"
                            >
                              <i className="fas fa-users"></i>
                              <span>{event.player_counts?.total || 0}</span>
                            </button>
                            {isEventPast(event) && (
                              <span style={{
                                padding: '4px 8px',
                                background: event.is_completed ? '#dcfce7' : '#fef3c7',
                                color: event.is_completed ? '#166534' : '#92400e',
                                borderRadius: '4px',
                                fontSize: '11px',
                                fontWeight: '500'
                              }}>
                                {event.is_completed ? '✓ Completed' : '⏳ Pending Results'}
                              </span>
                            )}
                          </div>
                        </td>
                        <td style={{ padding: '12px 10px' }}>
                          <div className="table-actions" style={{ display: 'flex', gap: '6px', justifyContent: 'center' }}>
                            {/* Edit Button - Disabled for past tournaments */}
                            <button
                              className="action-btn edit"
                              onClick={() => !isEditLocked(event) && setEditingEvent(event)}
                              title={isEditLocked(event) ? "Cannot edit - tournament date has started/passed" : "Edit Tournament"}
                              disabled={isEditLocked(event)}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                background: isEditLocked(event) ? '#f3f4f6' : 'white',
                                color: isEditLocked(event) ? '#9ca3af' : '#3b82f6',
                                cursor: isEditLocked(event) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: isEditLocked(event) ? 0.5 : 1
                              }}
                            >
                              <i className="fas fa-edit"></i>
                            </button>

                            {/* Unpublish Button - Disabled for past tournaments */}
                            <button
                              className="action-btn unpublish"
                              onClick={() => !isEventPast(event) && handleStatusUpdate(event.id, event.is_published ? 'draft' : 'published')}
                              title={isEventPast(event) ? "Cannot unpublish past tournament" : (event.is_published ? "Unpublish Tournament" : "Publish Tournament")}
                              disabled={isEventPast(event)}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                background: isEventPast(event) ? '#f3f4f6' : 'white',
                                color: isEventPast(event) ? '#9ca3af' : (event.is_published ? '#f59e0b' : '#10b981'),
                                cursor: isEventPast(event) ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: isEventPast(event) ? 0.5 : 1
                              }}
                            >
                              <i className={`fas fa-${event.is_published ? 'eye-slash' : 'eye'}`}></i>
                            </button>

                            {/* Result Button - Disabled for upcoming tournaments */}
                            <button
                              className="action-btn result"
                              onClick={() => isEventPast(event) && navigate(`/admin/events/${event.id}/results`)}
                              title={isEventPast(event) ? "View/Manage Results" : "Results available after tournament completion"}
                              disabled={!isEventPast(event)}
                              style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '6px',
                                border: '1px solid #e5e7eb',
                                background: isEventPast(event) ? 'white' : '#f3f4f6',
                                color: isEventPast(event) ? '#10b981' : '#9ca3af',
                                cursor: isEventPast(event) ? 'pointer' : 'not-allowed',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                transition: 'all 0.2s',
                                opacity: isEventPast(event) ? 1 : 0.5
                              }}
                            >
                              <i className="fas fa-trophy"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>


      {/* Templates Modal */}
      {showTemplates && (
        <div className="modal-overlay" onClick={() => setShowTemplates(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>Event Templates</h3>
              <button className="modal-close" onClick={() => setShowTemplates(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="templates-grid">
                {eventTemplates.map(template => (
                  <div key={template.id} className="template-card">
                    <div className="template-header">
                      <h4>{template.name}</h4>
                      <p>{template.description}</p>
                    </div>
                    <div className="template-preview">
                      <div className="preview-field">
                        <strong>Type:</strong> {template.data.event_type === 'N' ? 'National' : 'International'}
                      </div>
                      <div className="preview-field">
                        <strong>Level:</strong> {
                          template.data.event_level === 'LL' ? 'Little League' :
                          template.data.event_level === 'SJ' ? 'Sub Junior' :
                          template.data.event_level === 'JL' ? 'Junior' : 'Senior'
                        }
                      </div>
                      <div className="preview-field">
                        <strong>Organizer:</strong> {template.data.event_organizer}
                      </div>
                      <div className="preview-field">
                        <strong>Venue:</strong> {template.data.event_venue}
                      </div>
                    </div>
                    <div className="template-actions">
                      <button 
                        className="btn btn-primary btn-sm"
                        onClick={() => useTemplate(template)}
                        disabled={formLoading}
                      >
                        <i className="fas fa-magic"></i>
                        Use Template
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && confirmAction && (
        <div className="modal-overlay" onClick={() => setShowConfirmDialog(false)}>
          <div className="modal-content confirm-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{confirmAction.title}</h3>
              <button className="modal-close" onClick={() => setShowConfirmDialog(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              <div className="confirm-content">
                <div className="confirm-icon">
                  <i className={`fas ${confirmAction.type === 'closeEvent' ? 'fa-exclamation-triangle' : 'fa-question-circle'}`}></i>
                </div>
                <div className="confirm-message">
                  <h4>{confirmAction.message}</h4>
                  <p>{confirmAction.subMessage}</p>
                  {confirmAction.type === 'closeRegistration' && (
                    <div className="warning-note">
                      <i className="fas fa-info-circle"></i>
                      <span>Players will not be able to register for this event after closing.</span>
                    </div>
                  )}
                  {confirmAction.type === 'closeEvent' && (
                    <div className="warning-note danger">
                      <i className="fas fa-exclamation-triangle"></i>
                      <span>This event will be closed and unpublished. This action can be reversed later.</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button 
                className="btn btn-secondary" 
                onClick={() => setShowConfirmDialog(false)}
                disabled={formLoading}
              >
                {confirmAction.cancelText}
              </button>
              <button 
                className={`btn ${confirmAction.type === 'closeEvent' ? 'btn-danger' : 'btn-warning'}`}
                onClick={executeConfirmAction}
                disabled={formLoading}
              >
                {formLoading ? (
                  <>
                    <i className="fas fa-spinner fa-spin"></i>
                    Processing...
                  </>
                ) : (
                  <>
                    <i className={`fas ${confirmAction.type === 'closeEvent' ? 'fa-times-circle' : 'fa-user-times'}`}></i>
                    {confirmAction.confirmText}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Player Registrations Modal */}
      {showRegistrationsModal && (
        <div className="modal-overlay" onClick={() => setShowRegistrationsModal(false)}>
          <div className="modal-content large" onClick={(e) => e.stopPropagation()} style={{maxWidth: '1200px'}}>
            <div className="modal-header">
              <h3>
                <i className="fas fa-users"></i> Player Registrations - {selectedEventRegistrations?.event_name}
              </h3>
              <button className="modal-close" onClick={() => setShowRegistrationsModal(false)}>
                <i className="fas fa-times"></i>
              </button>
            </div>
            <div className="modal-body">
              {registrationsLoading ? (
                <div className="loading-content" style={{padding: '40px', textAlign: 'center'}}>
                  <div className="loading-spinner">
                    <i className="fas fa-spinner fa-spin" style={{fontSize: '32px', color: '#3b82f6'}}></i>
                  </div>
                  <p style={{marginTop: '16px'}}>Loading registrations...</p>
                </div>
              ) : registrations.length === 0 ? (
                <div className="empty-content" style={{padding: '40px', textAlign: 'center'}}>
                  <div className="empty-icon">
                    <i className="fas fa-user-slash" style={{fontSize: '48px', color: '#9ca3af'}}></i>
                  </div>
                  <h3>No Players Registered</h3>
                  <p>No players have registered for this event yet.</p>
                </div>
              ) : (
                <div>
                  <div style={{marginBottom: '20px', display: 'flex', gap: '16px'}}>
                    <div style={{padding: '12px 20px', background: '#3b82f6', color: 'white', borderRadius: '8px'}}>
                      <strong>Total:</strong> {registrations.length}
                    </div>
                    <div style={{padding: '12px 20px', background: '#f59e0b', color: 'white', borderRadius: '8px'}}>
                      <strong>Pending:</strong> {registrations.filter(r => r.registration_status === 'pending').length}
                    </div>
                    <div style={{padding: '12px 20px', background: '#10b981', color: 'white', borderRadius: '8px'}}>
                      <strong>Approved:</strong> {registrations.filter(r => r.registration_status === 'approved').length}
                    </div>
                    <div style={{padding: '12px 20px', background: '#ef4444', color: 'white', borderRadius: '8px'}}>
                      <strong>Rejected:</strong> {registrations.filter(r => r.registration_status === 'rejected').length}
                    </div>
                  </div>

                  <div className="events-table-container">
                    <table className="events-table professional-table">
                      <thead>
                        <tr>
                          <th>Reg ID</th>
                          <th>Player Name</th>
                          <th>Father Name</th>
                          <th>Age</th>
                          <th>Gender</th>
                          <th>State</th>
                          <th>Position</th>
                          <th>User Email</th>
                          <th>Status</th>
                          <th>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {registrations.map((reg) => (
                          <tr key={reg.id} style={{
                            background: reg.registration_status === 'approved' ? '#f0fdf4' :
                                       reg.registration_status === 'rejected' ? '#fef2f2' :
                                       reg.registration_status === 'pending' ? '#fef3c7' : 'white'
                          }}>
                            <td>{reg.player_reg_id}</td>
                            <td><strong>{reg.player_name}</strong></td>
                            <td>{reg.father_name}</td>
                            <td>{reg.age || 'N/A'}</td>
                            <td>{reg.gender === 'M' ? 'Male' : 'Female'}</td>
                            <td>{reg.state}</td>
                            <td>{reg.position || 'N/A'}</td>
                            <td>{reg.user_email}</td>
                            <td>
                              <span style={{
                                padding: '4px 12px',
                                borderRadius: '12px',
                                fontSize: '12px',
                                fontWeight: '600',
                                background: reg.registration_status === 'approved' ? '#10b981' :
                                           reg.registration_status === 'rejected' ? '#ef4444' : '#f59e0b',
                                color: 'white'
                              }}>
                                {reg.registration_status.toUpperCase()}
                              </span>
                            </td>
                            <td>
                              <div style={{display: 'flex', flexDirection: 'column', gap: '6px', alignItems: 'center'}}>
                                {reg.registration_status === 'pending' && (
                                  <>
                                    <button
                                      onClick={() => handleApproveRegistration(reg.player_id)}
                                      style={{
                                        background: '#10b981',
                                        color: 'white',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.2s',
                                        width: '100%',
                                        justifyContent: 'center',
                                        minWidth: '110px'
                                      }}
                                      onMouseOver={(e) => e.currentTarget.style.background = '#059669'}
                                      onMouseOut={(e) => e.currentTarget.style.background = '#10b981'}
                                      title="Approve"
                                    >
                                      <i className="fas fa-check"></i>
                                      <span>Approve</span>
                                    </button>
                                    <button
                                      onClick={() => handleRejectRegistration(reg.player_id)}
                                      style={{
                                        background: '#ef4444',
                                        color: 'white',
                                        border: 'none',
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        cursor: 'pointer',
                                        fontSize: '13px',
                                        fontWeight: '600',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '6px',
                                        transition: 'all 0.2s',
                                        width: '100%',
                                        justifyContent: 'center',
                                        minWidth: '110px'
                                      }}
                                      onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
                                      onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
                                      title="Reject"
                                    >
                                      <i className="fas fa-times"></i>
                                      <span>Reject</span>
                                    </button>
                                  </>
                                )}
                                {reg.registration_status === 'approved' && (
                                  <button
                                    onClick={() => handleRejectRegistration(reg.player_id)}
                                    style={{
                                      background: '#ef4444',
                                      color: 'white',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      transition: 'all 0.2s',
                                      justifyContent: 'center',
                                      minWidth: '110px'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#dc2626'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#ef4444'}
                                    title="Block Player"
                                  >
                                    <i className="fas fa-ban"></i>
                                    <span>Block</span>
                                  </button>
                                )}
                                {reg.registration_status === 'rejected' && (
                                  <button
                                    onClick={() => handleApproveRegistration(reg.player_id)}
                                    style={{
                                      background: '#10b981',
                                      color: 'white',
                                      border: 'none',
                                      padding: '6px 12px',
                                      borderRadius: '6px',
                                      cursor: 'pointer',
                                      fontSize: '13px',
                                      fontWeight: '600',
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: '6px',
                                      transition: 'all 0.2s',
                                      justifyContent: 'center',
                                      minWidth: '110px'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.background = '#059669'}
                                    onMouseOut={(e) => e.currentTarget.style.background = '#10b981'}
                                    title="Approve Player"
                                  >
                                    <i className="fas fa-check"></i>
                                    <span>Approve</span>
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default ProfessionalEventManagement